import React from 'react';
import { Box, Grid, Typography, Divider, alpha, Button } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Link as RouterLink } from 'react-router';

const gold = '#C0202A';
const silver = '#C0C0C0';

const serviceLinks = [
  { label: 'Auto & Marine Detailing', path: '/services/auto-detailing' },
  { label: 'Carpet Cleaning & Restoration', path: '/services/carpet-cleaning' },
  { label: 'Tile Cleaning & Restoration', path: '/services/tile-grout' },
  { label: 'Upholstery & Leather Care', path: '/services/upholstery-leather' },
];

const serviceAreas = [
  'Tucson', 'Marana', 'Oro Valley', 'Vail', 'Sahuarita', 'Green Valley', 'Sierra Vista',
];

export function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        background: '#050505',
        borderTop: `1px solid ${alpha(gold, 0.15)}`,
        pt: 10,
        pb: 4,
      }}
    >
      <Box sx={{ maxWidth: 1300, mx: 'auto', px: { xs: 3, md: 6 } }}>
        <Grid container spacing={6} sx={{ mb: 8 }}>
          {/* Brand Column */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: 3,
                    height: 36,
                    background: `linear-gradient(180deg, ${gold} 0%, ${alpha(gold, 0.3)} 100%)`,
                    mr: 0.5,
                    flexShrink: 0,
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontFamily: '"Cormorant Garamond", serif',
                      fontSize: '1.6rem',
                      fontWeight: 300,
                      letterSpacing: '0.12em',
                      color: '#F2EDE4',
                      lineHeight: 1.1,
                    }}
                  >
                    LINDSEY
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Montserrat", sans-serif',
                      fontSize: '0.55rem',
                      fontWeight: 600,
                      letterSpacing: '0.3em',
                      color: gold,
                      textTransform: 'uppercase',
                    }}
                  >
                    ENTERPRISES
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: alpha('#F2EDE4', 0.55), lineHeight: 1.9, mb: 3, maxWidth: 320 }}
              >
                Tucson's premier luxury cleaning and detailing company. IICRC Certified. Family owned and operated since 1994. We exceed expectations — every single time.
              </Typography>
            </Box>
          </Grid>

          {/* Services */}
          <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
            <Typography
              variant="overline"
              sx={{ color: gold, display: 'block', mb: 3 }}
            >
              Services
            </Typography>
            {serviceLinks.map((link) => (
              <Box
                key={link.label}
                component={RouterLink}
                to={link.path}
                sx={{
                  cursor: 'pointer',
                  display: 'block',
                  mb: 1.5,
                  color: alpha('#F2EDE4', 0.55),
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.8rem',
                  fontWeight: 300,
                  letterSpacing: '0.03em',
                  transition: 'all 0.2s',
                  background: 'transparent',
                  border: 0,
                  p: 0,
                  textAlign: 'left',
                  textDecoration: 'none',
                  '&:hover': {
                    color: gold,
                    pl: 1,
                  },
                }}
              >
                {link.label}
              </Box>
            ))}
          </Grid>

          {/* Service Areas */}
          <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
            <Typography
              variant="overline"
              sx={{ color: gold, display: 'block', mb: 3 }}
            >
              Service Areas
            </Typography>
            {serviceAreas.map((area) => (
              <Typography
                key={area}
                variant="body2"
                sx={{ color: alpha('#F2EDE4', 0.55), mb: 1, fontSize: '0.8rem', fontWeight: 300 }}
              >
                {area}
              </Typography>
            ))}
          </Grid>

          {/* Contact */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography
              variant="overline"
              sx={{ color: gold, display: 'block', mb: 3 }}
            >
              Get In Touch
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2.5 }}>
              <PhoneIcon sx={{ color: gold, fontSize: '1rem', mt: 0.3 }} />
              <Box>
                <Typography variant="body2" sx={{ color: alpha('#F2EDE4', 0.55), fontSize: '0.8rem', fontWeight: 300 }}>
                  Tucson, Arizona
                </Typography>
                <Typography variant="body2" sx={{ color: '#F2EDE4', fontSize: '0.85rem', fontWeight: 400, mt: 0.3 }}>
                  (520) 325-0576
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2.5 }}>
              <EmailIcon sx={{ color: gold, fontSize: '1rem', mt: 0.3 }} />
              <Box>
                <Typography variant="body2" sx={{ color: alpha('#F2EDE4', 0.55), fontSize: '0.8rem', fontWeight: 300 }}>
                  Email Us
                </Typography>
                <Typography variant="body2" sx={{ color: '#F2EDE4', fontSize: '0.85rem', fontWeight: 400, mt: 0.3 }}>
                  info@lindseyenterprisesaz.com
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 4 }}>
              <LocationOnIcon sx={{ color: gold, fontSize: '1rem', mt: 0.3 }} />
              <Typography variant="body2" sx={{ color: alpha('#F2EDE4', 0.55), fontSize: '0.8rem', fontWeight: 300 }}>
                Serving Tucson & Surrounding Areas Since 1994
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/contact#booking-form"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ py: 1.5, fontSize: '0.65rem', letterSpacing: '0.2em' }}
            >
              Schedule Service
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: alpha(gold, 0.1), mb: 4 }} />

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ color: alpha('#F2EDE4', 0.3), fontSize: '0.72rem', fontWeight: 300, letterSpacing: '0.05em' }}>
            © {new Date().getFullYear()} Lindsey Enterprises. All rights reserved. · IICRC Certified · Tucson, Arizona
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {['Privacy Policy', 'Terms of Service'].map((item) => (
              <Typography
                key={item}
                variant="body2"
                sx={{
                  color: alpha('#F2EDE4', 0.3),
                  fontSize: '0.72rem',
                  cursor: 'pointer',
                  '&:hover': { color: gold },
                }}
              >
                {item}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
